import { styleChoiceSchema, type PlatformAppearance, type StyleChoice } from '@base/shared';

/**
 * O visual de passagem (ADR 0140): quem chega pela vitrine do Smart Builder vê o sistema com o
 * estilo do próprio sistema dela, para experimentar um módulo como ele ficaria lá. O estilo vem no
 * fragmento do endereço (`#estilo=<base64url do JSON>`), que nunca vai ao servidor e atravessa o
 * redirecionamento do acesso de uso único. Vale só nesta aba (sessionStorage) e só muda o visual:
 * nada é gravado no sistema.
 */
const PREFIX = '#estilo=';
const STORAGE_KEY = 'base.visitor-style';
const MAX_LENGTH = 4000;

/** Lê o estilo do fragmento; null se não há ou está fora do formato. */
export function visitorStyleFromHash(hash: string): StyleChoice | null {
  if (!hash.startsWith(PREFIX) || hash.length > MAX_LENGTH) return null;
  try {
    const b64 = hash.slice(PREFIX.length).replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    const parsed = styleChoiceSchema.safeParse(JSON.parse(new TextDecoder().decode(bytes)));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** O visual gravado com o de passagem por cima: o estilo dele e nenhuma cor ou tema próprio. */
export function withVisitorStyle(
  appearance: PlatformAppearance,
  style: StyleChoice | null,
): PlatformAppearance {
  if (!style) return appearance;
  return { ...appearance, theme: null, style, accentColor: null, paperColor: null, inkColor: null };
}

let current: StyleChoice | null | undefined;

/**
 * O estilo de passagem desta aba. Na primeira chamada, lê o fragmento (e o tira do endereço) ou o
 * que a aba já guardou; depois, devolve o mesmo.
 */
export function visitorStyle(): StyleChoice | null {
  if (current !== undefined) return current;
  current = null;
  if (typeof window === 'undefined') return current;
  const fromHash = visitorStyleFromHash(window.location.hash);
  try {
    if (fromHash) {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromHash));
      const { pathname, search } = window.location;
      window.history.replaceState(window.history.state, '', `${pathname}${search}`);
      current = fromHash;
    } else {
      const saved = window.sessionStorage.getItem(STORAGE_KEY);
      const parsed = saved ? styleChoiceSchema.safeParse(JSON.parse(saved)) : null;
      current = parsed?.success ? parsed.data : null;
    }
  } catch {
    current = fromHash;
  }
  return current;
}
